// ==========================
// Version 1 - nft/status.js
// - Shared NFT mint-status helpers
// - Firestore remains a cache; chain reconciliation can confirm mint status
// - Uses metadata.memory_id as fallback when Firestore NFT fields are missing
// ==========================

/* global BigInt */

import { getContract } from 'thirdweb';
import { polygonAmoy } from 'thirdweb/chains';
import { getNFT, getNFTs } from 'thirdweb/extensions/erc721';
import { thirdwebClient } from '../thirdwebClient';
import { NFT_COLLECTION_ADDRESS } from '../thirdweb.config';

const NFT_NETWORK = 'polygon-amoy';
const NFT_CHAIN_ID = 80002;
const MAX_RECONCILE_NFTS = 200;

let _contract = null;

function getNftContract() {
  if (_contract) return _contract;
  _contract = getContract({
    client: thirdwebClient,
    address: NFT_COLLECTION_ADDRESS,
    chain: polygonAmoy,
  });
  return _contract;
}

function normalizeAddress(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

function normalizeTokenUri(value) {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('ipfs://')) {
    return `ipfs://${trimmed.replace(/^ipfs:\/\//, '').replace(/^ipfs\//, '').replace(/^\/+/, '')}`;
  }

  const lighthouseMatch = trimmed.match(/^https?:\/\/[^/]+\/ipfs\/(.+)$/i);
  if (lighthouseMatch?.[1]) {
    return `ipfs://${lighthouseMatch[1].replace(/^\/+/, '')}`;
  }

  return trimmed;
}

function coerceTokenId(value) {
  if (value == null || value === '') return null;
  try {
    return BigInt(String(value));
  } catch (_) {
    return null;
  }
}

function getMetadataAttributes(metadata) {
  return Array.isArray(metadata?.attributes) ? metadata.attributes : [];
}

function getMetadataMemoryId(metadata) {
  const attrs = getMetadataAttributes(metadata);
  const attr = attrs.find((item) => String(item?.trait_type || '').toLowerCase() === 'memory_id');
  const value = attr?.value ?? metadata?.memory_id ?? null;
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

function getMetadataMinter(metadata) {
  const attrs = getMetadataAttributes(metadata);
  const attr = attrs.find((item) => String(item?.trait_type || '').toLowerCase() === 'minter');
  return normalizeAddress(attr?.value ?? metadata?.minter ?? null);
}

function getMemoryId(memory) {
  const value = memory?.firestoreId || memory?.id || null;
  if (!value) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

function deriveExplorerUrl(txHash) {
  if (!txHash) return null;
  const clean = String(txHash).trim();
  return clean ? `https://amoy.polygonscan.com/tx/${clean}` : null;
}

export function getMemoryNftLatest(memory) {
  return memory?.nft?.latest || memory?.nft?.lastMint || null;
}

export function buildMintRecord(input = {}) {
  const tokenId = input?.tokenId != null && input.tokenId !== '' ? String(input.tokenId) : null;
  const txHash = input?.txHash ? String(input.txHash).trim() : null;
  const tokenUri = normalizeTokenUri(input?.tokenUri || input?.tokenURI || null);
  const metadataCid = input?.metadataCid ? String(input.metadataCid).trim() : null;
  const imageCid = input?.imageCid ? String(input.imageCid).trim() : null;
  const provider = input?.provider ? String(input.provider).trim() : null;
  const gatewayUrl = input?.gatewayUrl ? String(input.gatewayUrl).trim() : null;
  const toAddress = normalizeAddress(input?.toAddress || input?.owner || null);
  const minterAddress = normalizeAddress(input?.minterAddress || null);
  const mintedAt = input?.mintedAt || input?.savedAt || null;
  const mirror =
    input?.mirror && typeof input.mirror === 'object'
      ? {
          provider: input.mirror?.provider ? String(input.mirror.provider).trim() : null,
          metadataCid: input.mirror?.metadataCid ? String(input.mirror.metadataCid).trim() : null,
          imageCid: input.mirror?.imageCid ? String(input.mirror.imageCid).trim() : null,
          tokenUri: input.mirror?.tokenUri ? normalizeTokenUri(input.mirror.tokenUri) : null,
          gatewayBase: input.mirror?.gatewayBase ? String(input.mirror.gatewayBase).trim() : null,
          gatewayUrl: input.mirror?.gatewayUrl ? String(input.mirror.gatewayUrl).trim() : null,
        }
      : null;

  return {
    status: input?.status || 'success',
    chainId: input?.chainId ?? NFT_CHAIN_ID,
    network: input?.network || NFT_NETWORK,
    contract: input?.contract || input?.contractAddress || NFT_COLLECTION_ADDRESS,
    contractAddress: input?.contractAddress || input?.contract || NFT_COLLECTION_ADDRESS,
    tokenId,
    txHash,
    explorerUrl: input?.explorerUrl || deriveExplorerUrl(txHash),
    tokenUri,
    metadataCid,
    imageCid,
    provider,
    gatewayUrl,
    toAddress,
    minterAddress,
    mintedAt,
    mirror,
  };
}

export function isMemoryMarkedMinted(memory) {
  const latest = buildMintRecord(getMemoryNftLatest(memory) || {});
  return !!(latest.txHash || latest.tokenId != null || latest.tokenUri);
}

function buildChainRecord(nft, { txHash = null, metadataCid = null, imageCid = null } = {}) {
  const metadata = nft?.metadata || {};
  return buildMintRecord({
    chainId: nft?.chainId ?? NFT_CHAIN_ID,
    network: NFT_NETWORK,
    contractAddress: nft?.tokenAddress || NFT_COLLECTION_ADDRESS,
    tokenId: typeof nft?.id === 'bigint' ? nft.id.toString() : nft?.id ?? null,
    txHash,
    tokenUri: nft?.tokenURI || metadata?.uri || null,
    metadataCid,
    imageCid,
    toAddress: nft?.owner || null,
    minterAddress: getMetadataMinter(metadata),
    mintedAt: metadata?.minted_at || null,
  });
}

async function findChainMatchByTokenId(latest, memoryId, ownerAddress) {
  const tokenId = coerceTokenId(latest?.tokenId);
  if (tokenId == null) return null;

  const nft = await getNFT({
    contract: getNftContract(),
    tokenId,
    includeOwner: true,
    useIndexer: false,
  });

  if (!nft) return null;

  const chainRecord = buildChainRecord(nft, {
    txHash: latest?.txHash || null,
    metadataCid: latest?.metadataCid || null,
    imageCid: latest?.imageCid || null,
  });

  const sameTokenUri =
    !latest?.tokenUri || !chainRecord.tokenUri || normalizeTokenUri(latest.tokenUri) === chainRecord.tokenUri;

  const chainMemoryId = getMetadataMemoryId(nft?.metadata);
  const sameMemoryId = !memoryId || !chainMemoryId || chainMemoryId === memoryId;

  const normalizedOwner = normalizeAddress(ownerAddress);
  const sameOwner = !normalizedOwner || !chainRecord.toAddress || chainRecord.toAddress === normalizedOwner;

  return sameTokenUri && sameMemoryId && sameOwner ? chainRecord : null;
}

async function findChainMatchByScan(memoryId, latest, ownerAddress) {
  const nfts = await getNFTs({
    contract: getNftContract(),
    start: 0,
    count: MAX_RECONCILE_NFTS,
    includeOwners: true,
    useIndexer: false,
  });

  const normalizedMemoryId = memoryId ? String(memoryId) : null;
  const normalizedOwner = normalizeAddress(ownerAddress);
  const normalizedTokenUri = normalizeTokenUri(latest?.tokenUri || null);

  const match = (nfts || []).find((nft) => {
    const metadataMemoryId = getMetadataMemoryId(nft?.metadata);
    const chainTokenUri = normalizeTokenUri(nft?.tokenURI || nft?.metadata?.uri || null);
    const chainOwner = normalizeAddress(nft?.owner || null);

    if (normalizedMemoryId && metadataMemoryId === normalizedMemoryId) {
      if (!normalizedOwner || !chainOwner || chainOwner === normalizedOwner) {
        return true;
      }
    }

    if (normalizedTokenUri && chainTokenUri === normalizedTokenUri) {
      if (!normalizedOwner || !chainOwner || chainOwner === normalizedOwner) {
        return true;
      }
    }

    return false;
  });

  return match ? buildChainRecord(match, {
    txHash: latest?.txHash || null,
    metadataCid: latest?.metadataCid || null,
    imageCid: latest?.imageCid || null,
  }) : null;
}

export async function resolveMemoryMintStatus({ memory, ownerAddress = null } = {}) {
  const latest = buildMintRecord(getMemoryNftLatest(memory) || {});
  const persisted = !!(latest.txHash || latest.tokenId != null || latest.tokenUri);
  const memoryId = getMemoryId(memory);

  let chainLatest = null;

  try {
    if (latest.tokenId != null) {
      chainLatest = await findChainMatchByTokenId(latest, memoryId, ownerAddress);
    }

    if (!chainLatest && (memoryId || latest.tokenUri)) {
      chainLatest = await findChainMatchByScan(memoryId, latest, ownerAddress);
    }
  } catch (error) {
    console.warn('[BWK][NFT] Chain reconciliation failed:', error?.message || error);
  }

  if (chainLatest) {
    return {
      isMinted: true,
      latest: { ...latest, ...chainLatest },
      source: persisted ? 'firestore' : 'chain',
      chainConfirmed: true,
    };
  }

  return {
    isMinted: persisted,
    latest: persisted ? latest : null,
    source: persisted ? 'firestore' : 'none',
    chainConfirmed: false,
  };
}

// ==========================
// End Version 1 - nft/status.js
// ==========================
